import React, { useMemo } from 'react';
import { 
  View, 
  Text, 
  ScrollView, 
  TouchableOpacity, 
  Dimensions 
} from 'react-native';
import { 
  XStack, 
  YStack, 
  Card, 
  Progress,
  Circle
} from 'tamagui';
import { 
  MaterialIcons, 
  Feather, 
  AntDesign,
  MaterialCommunityIcons 
} from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';

interface Task {
  _id: string;
  title: string;
  status: 'todo' | 'in-progress' | 'review' | 'done';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignedTo?: string[];
  dueDate?: string;
  createdAt: string;
  timeTracking?: {
    estimated: number;
    actual: number;
  };
}

interface WorkPackage {
  _id: string;
  title: string;
  tasks: Task[];
  status: 'active' | 'completed' | 'archived';
  progress: number;
}

interface Project {
  _id: string;
  title: string;
  status: string;
  progress: number;
  startDate: string;
  endDate: string;
  team: any[];
}

interface ModernProjectAnalyticsProps {
  project: Project;
  tasks: Task[];
  workPackages: WorkPackage[];
  onTaskEdit?: (task: Task) => void;
  onTaskCreate?: () => void;
  detailed?: boolean;
}

const ModernProjectAnalytics: React.FC<ModernProjectAnalyticsProps> = ({
  project,
  tasks,
  workPackages,
  onTaskEdit,
  onTaskCreate,
  detailed = false
}) => {
  const theme = themeSelector();
  const { width: screenWidth } = Dimensions.get('window');
  const isTablet = screenWidth > 768;

  // Calculate analytics
  const analytics = useMemo(() => {
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(task => task.status === 'done').length;
    const inProgressTasks = tasks.filter(task => task.status === 'in-progress').length;
    const todoTasks = tasks.filter(task => task.status === 'todo').length;
    const reviewTasks = tasks.filter(task => task.status === 'review').length;

    const overdueTasks = tasks.filter(task => {
      if (!task.dueDate) return false;
      return new Date(task.dueDate) < new Date() && task.status !== 'done';
    }).length;

    const priorityBreakdown = {
      urgent: tasks.filter(task => task.priority === 'urgent').length,
      high: tasks.filter(task => task.priority === 'high').length,
      medium: tasks.filter(task => task.priority === 'medium').length,
      low: tasks.filter(task => task.priority === 'low').length,
    };

    const assignmentStats = tasks.reduce((acc, task) => {
      if (task.assignedTo?.length) {
        task.assignedTo.forEach(userId => {
          acc[userId] = (acc[userId] || 0) + 1;
        });
      } else {
        acc['unassigned'] = (acc['unassigned'] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    const timeTracking = tasks.reduce((acc, task) => {
      if (task.timeTracking) {
        acc.estimated += task.timeTracking.estimated || 0;
        acc.actual += task.timeTracking.actual || 0;
      }
      return acc;
    }, { estimated: 0, actual: 0 });

    const velocity = completedTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    const workPackageProgress = workPackages.map(wp => ({
      ...wp,
      completionRate: wp.tasks.length > 0 
        ? (wp.tasks.filter(task => task.status === 'done').length / wp.tasks.length) * 100 
        : 0
    }));

    return {
      totalTasks,
      completedTasks,
      inProgressTasks,
      todoTasks,
      reviewTasks,
      overdueTasks,
      priorityBreakdown,
      assignmentStats,
      timeTracking,
      velocity,
      workPackageProgress,
      completionRate: totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0
    };
  }, [tasks, workPackages]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'todo': return '#6b7280';
      case 'in-progress': return '#3b82f6';
      case 'review': return '#f59e0b';
      case 'done': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#eab308';
      case 'low': return '#22c55e';
      default: return '#6b7280';
    }
  };

  const MetricCard = ({ title, value, subtitle, color, icon, trend }: {
    title: string;
    value: string | number;
    subtitle?: string;
    color: string;
    icon: string;
    trend?: string;
  }) => (
    <Animatable.View 
      animation="fadeInUp" 
      duration={600}
      style={{
        flex: 1,
        minWidth: isTablet ? 200 : 150,
        margin: 8,
      }}
    >
      <Card
        padding="$4"
        backgroundColor={theme ? '#ffffff' : '#1f2937'}
        borderColor={theme ? '#e5e7eb' : '#374151'}
        borderWidth={1}
        borderRadius="$3"
        shadowColor="#000"
        shadowOffset={{ width: 0, height: 2 }}
        shadowOpacity={0.1}
        shadowRadius={4}
        elevation={2}
      >
        <XStack justifyContent="space-between" alignItems="flex-start">
          <YStack flex={1}>
            <Text style={{
              fontSize: 12,
              fontWeight: '500',
              color: theme ? '#6b7280' : '#9ca3af',
              marginBottom: 4,
            }}>
              {title}
            </Text>
            <Text style={{
              fontSize: 24,
              fontWeight: '700',
              color: theme ? '#111827' : '#ffffff',
              marginBottom: 2,
            }}>
              {value}
            </Text>
            {subtitle && (
              <Text style={{
                fontSize: 11,
                color: theme ? '#6b7280' : '#9ca3af',
              }}>
                {subtitle}
              </Text>
            )}
            {trend && (
              <Text style={{
                fontSize: 11,
                fontWeight: '500',
                color: trend.startsWith('+') ? '#10b981' : '#ef4444',
                marginTop: 4,
              }}>
                {trend}
              </Text>
            )}
          </YStack>
          <View style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: color + '20',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <MaterialIcons name={icon as any} size={20} color={color} />
          </View>
        </XStack>
      </Card>
    </Animatable.View>
  );

  const ChartCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Card
      padding="$4"
      backgroundColor={theme ? '#ffffff' : '#1f2937'}
      borderColor={theme ? '#e5e7eb' : '#374151'}
      borderWidth={1}
      borderRadius="$3"
      marginBottom="$4"
      shadowColor="#000"
      shadowOffset={{ width: 0, height: 2 }}
      shadowOpacity={0.1}
      shadowRadius={4}
      elevation={2}
    >
      <Text style={{
        fontSize: 16,
        fontWeight: '600',
        color: theme ? '#111827' : '#ffffff',
        marginBottom: 16,
      }}>
        {title}
      </Text>
      {children}
    </Card>
  );

  const ProgressBar = ({ label, value, color, total }: {
    label: string;
    value: number;
    color: string;
    total: number;
  }) => (
    <YStack gap="$2" marginBottom="$3">
      <XStack justifyContent="space-between" alignItems="center">
        <Text style={{
          fontSize: 14,
          fontWeight: '500',
          color: theme ? '#111827' : '#ffffff',
        }}>
          {label}
        </Text>
        <Text style={{
          fontSize: 12,
          color: theme ? '#6b7280' : '#9ca3af',
        }}>
          {value} / {total}
        </Text>
      </XStack>
      <View style={{
        height: 8,
        backgroundColor: theme ? '#e5e7eb' : '#374151',
        borderRadius: 4,
        overflow: 'hidden',
      }}>
        <View style={{
          height: '100%',
          width: total > 0 ? `${(value / total) * 100}%` : '0%',
          backgroundColor: color,
          borderRadius: 4,
        }} />
      </View>
    </YStack>
  );

  return (
    <ScrollView
      style={{ 
        flex: 1, 
        backgroundColor: theme ? '#f9fafb' : '#111827'
      }}
      showsVerticalScrollIndicator={false}
    >
      <View style={{ padding: 20 }}>
        {/* Key Metrics */}
        <Text style={{
          fontSize: 20,
          fontWeight: '700',
          color: theme ? '#111827' : '#ffffff',
          marginBottom: 16,
        }}>
          Project Overview
        </Text>

        <View style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          marginHorizontal: -8,
          marginBottom: 24,
        }}>
          <MetricCard
            title="Total Tasks"
            value={analytics.totalTasks}
            color="#3b82f6"
            icon="assignment"
          />
          
          <MetricCard
            title="Completed"
            value={analytics.completedTasks}
            subtitle={`${Math.round(analytics.completionRate)}% complete`}
            color="#10b981"
            icon="check-circle"
          />
          
          <MetricCard
            title="In Progress"
            value={analytics.inProgressTasks}
            color="#f59e0b"
            icon="timelapse"
          />
          
          <MetricCard
            title="Overdue"
            value={analytics.overdueTasks}
            color="#ef4444"
            icon="warning"
          />

          {detailed && (
            <>
              <MetricCard
                title="Team Members"
                value={project?.team?.length || 0}
                color="#8b5cf6"
                icon="group"
              />
              
              <MetricCard
                title="Work Packages"
                value={workPackages.length}
                color="#06b6d4"
                icon="folder"
              />
            </>
          )}
        </View>

        {/* Task Status Breakdown */}
        <ChartCard title="Task Status Distribution">
          <ProgressBar
            label="To Do"
            value={analytics.todoTasks}
            color={getStatusColor('todo')}
            total={analytics.totalTasks}
          />
          <ProgressBar
            label="In Progress"
            value={analytics.inProgressTasks}
            color={getStatusColor('in-progress')}
            total={analytics.totalTasks}
          />
          <ProgressBar
            label="Review"
            value={analytics.reviewTasks}
            color={getStatusColor('review')}
            total={analytics.totalTasks}
          />
          <ProgressBar
            label="Done"
            value={analytics.completedTasks}
            color={getStatusColor('done')}
            total={analytics.totalTasks}
          />
        </ChartCard>

        {/* Priority Breakdown */}
        <ChartCard title="Priority Distribution">
          <ProgressBar
            label="Urgent"
            value={analytics.priorityBreakdown.urgent}
            color={getPriorityColor('urgent')}
            total={analytics.totalTasks}
          />
          <ProgressBar
            label="High"
            value={analytics.priorityBreakdown.high}
            color={getPriorityColor('high')}
            total={analytics.totalTasks}
          />
          <ProgressBar
            label="Medium"
            value={analytics.priorityBreakdown.medium}
            color={getPriorityColor('medium')}
            total={analytics.totalTasks}
          />
          <ProgressBar
            label="Low"
            value={analytics.priorityBreakdown.low}
            color={getPriorityColor('low')}
            total={analytics.totalTasks}
          />
        </ChartCard>

        {/* Work Package Progress */}
        {workPackages.length > 0 && (
          <ChartCard title="Work Package Progress">
            {analytics.workPackageProgress.map((wp, index) => (
              <YStack key={wp._id} gap="$2" marginBottom="$3">
                <XStack justifyContent="space-between" alignItems="center">
                  <Text style={{
                    fontSize: 14,
                    fontWeight: '500',
                    color: theme ? '#111827' : '#ffffff',
                  }} numberOfLines={1}>
                    {wp.title}
                  </Text>
                  <Text style={{
                    fontSize: 12,
                    color: theme ? '#6b7280' : '#9ca3af',
                  }}>
                    {Math.round(wp.completionRate)}%
                  </Text>
                </XStack>
                <Progress 
                  value={wp.completionRate} 
                  size="$2" 
                  backgroundColor={theme ? '#e5e7eb' : '#374151'}
                >
                  <Progress.Indicator 
                    backgroundColor={wp.completionRate >= 100 ? '#10b981' : '#3b82f6'} 
                  />
                </Progress>
              </YStack>
            ))}
          </ChartCard>
        )}

        {detailed && (
          <>
            {/* Time Tracking */}
            {analytics.timeTracking.estimated > 0 && (
              <ChartCard title="Time Tracking">
                <XStack justifyContent="space-between" marginBottom="$3">
                  <YStack alignItems="center" flex={1}>
                    <Text style={{
                      fontSize: 24,
                      fontWeight: '700',
                      color: '#3b82f6',
                    }}>
                      {analytics.timeTracking.estimated}h
                    </Text>
                    <Text style={{
                      fontSize: 12,
                      color: theme ? '#6b7280' : '#9ca3af',
                    }}>
                      Estimated
                    </Text>
                  </YStack>
                  
                  <YStack alignItems="center" flex={1}>
                    <Text style={{
                      fontSize: 24,
                      fontWeight: '700',
                      color: analytics.timeTracking.actual > analytics.timeTracking.estimated ? '#ef4444' : '#10b981',
                    }}>
                      {analytics.timeTracking.actual}h
                    </Text>
                    <Text style={{
                      fontSize: 12,
                      color: theme ? '#6b7280' : '#9ca3af',
                    }}>
                      Actual
                    </Text>
                  </YStack>
                  
                  <YStack alignItems="center" flex={1}>
                    <Text style={{
                      fontSize: 24,
                      fontWeight: '700',
                      color: analytics.timeTracking.actual <= analytics.timeTracking.estimated ? '#10b981' : '#ef4444',
                    }}>
                      {analytics.timeTracking.estimated > 0 
                        ? Math.round((analytics.timeTracking.actual / analytics.timeTracking.estimated) * 100)
                        : 0
                      }%
                    </Text>
                    <Text style={{
                      fontSize: 12,
                      color: theme ? '#6b7280' : '#9ca3af',
                    }}>
                      Efficiency
                    </Text>
                  </YStack>
                </XStack>
              </ChartCard>
            )}

            {/* Assignment Distribution */}
            {Object.keys(analytics.assignmentStats).length > 0 && (
              <ChartCard title="Task Assignment">
                {Object.entries(analytics.assignmentStats)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 5)
                  .map(([userId, count]) => (
                    <ProgressBar
                      key={userId}
                      label={userId === 'unassigned' ? 'Unassigned' : userId}
                      value={count}
                      color={userId === 'unassigned' ? '#6b7280' : '#3b82f6'}
                      total={analytics.totalTasks}
                    />
                  ))}
              </ChartCard>
            )}
          </>
        )}

        {/* Quick Actions */}
        {!detailed && (
          <Card
            padding="$4"
            backgroundColor={theme ? '#ffffff' : '#1f2937'}
            borderColor={theme ? '#e5e7eb' : '#374151'}
            borderWidth={1}
            borderRadius="$3"
            marginBottom="$4"
          >
            <Text style={{
              fontSize: 16,
              fontWeight: '600',
              color: theme ? '#111827' : '#ffffff',
              marginBottom: 16,
            }}>
              Quick Actions
            </Text>
            
            <XStack gap="$3" flexWrap="wrap">
              <TouchableOpacity
                onPress={onTaskCreate}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  paddingHorizontal: 16,
                  paddingVertical: 12,
                  backgroundColor: '#3b82f6',
                  borderRadius: 8,
                  flex: 1,
                  minWidth: 140,
                }}
              >
                <MaterialIcons name="add" size={20} color="#ffffff" />
                <Text style={{
                  color: '#ffffff',
                  fontSize: 14,
                  fontWeight: '500',
                }}>
                  New Task
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  paddingHorizontal: 16,
                  paddingVertical: 12,
                  backgroundColor: theme ? '#f3f4f6' : '#374151',
                  borderRadius: 8,
                  flex: 1,
                  minWidth: 140,
                }}
              >
                <MaterialIcons 
                  name="assessment" 
                  size={20} 
                  color={theme ? '#111827' : '#ffffff'} 
                />
                <Text style={{
                  color: theme ? '#111827' : '#ffffff',
                  fontSize: 14,
                  fontWeight: '500',
                }}>
                  View Report
                </Text>
              </TouchableOpacity>
            </XStack>
          </Card>
        )}
      </View>
    </ScrollView>
  );
};

export default ModernProjectAnalytics;
